/**
 * Settings module constants.
 *
 * Every value the settings UI switches on lives here rather than as a repeated
 * string literal, so the sidebar, the hub cards and the panels can never drift
 * apart from each other.
 */

import {
  faCheck,
  faCircleCheck,
  faCircleInfo,
  faCircleXmark,
  faClock,
  faFileSignature,
  faGem,
  faGlobe,
  faRightLeft,
  faSignature,
  faTriangleExclamation,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { NOTICE_TONE } from "components/shared/NoticeBanner";
import { LANGUAGE } from "services/general";

/**
 * Tab identifiers — what `resolveStatus` / `SettingCard` branch on. A tab's URL
 * is its `SETTINGS_TAB_PATHS` segment; `?tab=<name>` links from before tabs had
 * routes still resolve, through the hub's redirect.
 */
export const SETTINGS_TAB_NAMES = {
  GENERAL_SETTINGS: "GeneralSettings",
  COMMUNICATION_CHANNELS: "CommunicationChannels",
  DOCUMENTS_PREFIXES: "DocumentsPrefixes",
  BILL_INFO: "BillInfo",
  CONTRACT_SERIAL_PREFIXES: "ContractSerialPrefixes",
  HOME_SET: "HomeSet",
  LANDING_PAGE_CONTROLS: "LandingPageControls",
  PAYMENT_GATEWAY_SETUP: "PaymentGatewaySetup",
  ZACTA: "Zacta",
  EJAR_CREDENTIALS: "EjarCredentials",
  CRM_SETTINGS: "CrmSettings",
  SITE_VISIT_SETTINGS: "SiteVisitSettings",
  NAFATH: "NafathCredentials",
  HR_SETTINGS: "HrSettings",
  ADS_CROSS_LISTING: "AdsCrossListing",
  LETTER_SETTINGS: "LetterSettings",
  DATA_MIGRATION: "DataMigration",
  SADQ: "SadqSettings",
  FISCAL_YEAR: "FiscalYearSettings",
  MAINTENANCE_SETTINGS: "MaintenanceSettings",
  SMS_PROVIDER: "SmsProvider",
  PRIVATE_DOMAIN: "PrivateDomain",
};

/**
 * The external listing apps an advertisement can be cross-listed to. Mirrors
 * `App\Services\CrossListing\Settings\CrossListingSettings::PORTALS` — the
 * backend saves one portal per request and validates against that list, so a
 * value here that is not there is refused with a 422.
 */
export const CROSS_LISTING_PORTALS = {
  AQAR: "aqar",
};

/**
 * Each portal's public site, for the "visit" link on its settings card. Keyed
 * by portal so a second app's URL arrives with its key rather than being typed
 * into the panel that renders it.
 */
export const CROSS_LISTING_PORTAL_SITES = {
  [CROSS_LISTING_PORTALS.AQAR]: "https://sa.aqar.fm",
};

/**
 * The integrations the office activates by paying a one-time wallet fee.
 * Mirrors `App\Enums\IntegrationType` — `dashboard/agent/integrations` keys
 * every entry by these values, so a literal here that is not there matches no
 * integration and reads as "not activated" on a tenant that has activated it.
 */
export const INTEGRATION_TYPE = {
  ZATCA: "zatca",
  PAYMENT_GATEWAY: "payment_gateway",
  EJAR: "ejar",
  NAFATH: "nafath",
  SMS_PROVIDER: "sms_provider",
  PRIVATE_DOMAIN: "private_domain",
};

/**
 * Sadq's own site, for the "visit" link on the e-signature settings card.
 */
export const SADQ_SITE = "https://sadq.sa";

/**
 * Msegat's own site, for the "visit" link on the SMS provider screens.
 */
export const MSEGAT_SITE = "https://www.msegat.com";

export const SMS_PROVIDER_STATUS = {
  NOT_CONNECTED: "not_connected",
  REQUESTED: "requested",
  CONNECTED: "connected",
};

const SMS_PROVIDER_STATUS_VALUES = Object.values(SMS_PROVIDER_STATUS);

/** Whether `status` is one this frontend has copy and a tone for. */
export const isKnownSmsProviderStatus = (status) =>
  SMS_PROVIDER_STATUS_VALUES.includes(status);

/**
 * The `StatsCard` accent the connection-status card takes per status. Read
 * through `isKnownSmsProviderStatus` first — an unknown status takes `slate`.
 */
export const SMS_PROVIDER_STATUS_ACCENT = {
  [SMS_PROVIDER_STATUS.CONNECTED]: "success",
  [SMS_PROVIDER_STATUS.REQUESTED]: "warning",
  [SMS_PROVIDER_STATUS.NOT_CONNECTED]: "slate",
};

/** The longest test message the tab accepts, in characters. */
export const SMS_TEST_MESSAGE_MAX = 1000;

export const SMS_REQUEST_DOCUMENT = {
  AUTHORIZATION_LETTER: "authorization_letter",
};

export const SMS_REQUEST_FILE_TYPES = ["pdf", "jpg", "jpeg", "png"];
export const SMS_REQUEST_FILE_ACCEPT = SMS_REQUEST_FILE_TYPES.map(
  (type) => `.${type}`
).join(",");
export const SMS_REQUEST_FILE_MAX_MB = 5;

export const SMS_PROVIDER_FIELD_MAX = {
  SENDER_NAME: 50,
  USERNAME: 100,
  API_KEY: 255,
  NOTES: 2000,
};

export const SMS_PROVIDER_TAB = {
  TEST: "test",
  SETTINGS: "settings",
};

export const SMS_PROVIDER_VIEW = {
  CONNECT: "connect",
  MANAGE: "manage",
};

export const SMS_CONNECT_MODE = {
  ACCOUNT: "account",
  REQUEST: "request",
};

/** What the API key box shows once a key is stored — the key is never sent back. */
export const SMS_PROVIDER_SECRET_PLACEHOLDER = "••••••••••••";

export const SMS_REQUEST_FIELDS = [
  "contact_name",
  "contact_mobile",
  "contact_email",
  "sender_name",
  "promotional_sender_name",
  "notes",
];

/** The service request's steps, by id — everything is keyed on these, not on position. */
export const SMS_REQUEST_STEP = {
  CONTACT: "contact",
  SENDER: "sender",
  DOCUMENTS: "documents",
};

/** The order the steps are shown in; the rail and Next / Back walk this. */
export const SMS_REQUEST_STEP_ORDER = [
  SMS_REQUEST_STEP.CONTACT,
  SMS_REQUEST_STEP.SENDER,
  SMS_REQUEST_STEP.DOCUMENTS,
];

export const SMS_REQUEST_STEP_FIELDS = {
  [SMS_REQUEST_STEP.CONTACT]: [
    "contact_name",
    "contact_mobile",
    "contact_email",
  ],
  [SMS_REQUEST_STEP.SENDER]: ["sender_name", "promotional_sender_name"],
  [SMS_REQUEST_STEP.DOCUMENTS]: ["notes"],
};

export const SMS_REQUEST_STEP_SECTIONS = {
  [SMS_REQUEST_STEP.CONTACT]: { titleKey: "contactSection", icon: faUser },
  [SMS_REQUEST_STEP.SENDER]: {
    titleKey: "senderSection",
    hintKey: "senderHint",
    icon: faSignature,
  },
  [SMS_REQUEST_STEP.DOCUMENTS]: {
    titleKey: "documentsSection",
    icon: faFileSignature,
  },
};

/**
 * Where the office's private domain is, as `GET dashboard/agent/settings/private-domain`
 * reports it in `status`.
 */
export const PRIVATE_DOMAIN_STATUS = {
  NOT_CONNECTED: "not_connected",
  REGISTERING: "registering",
  PENDING_NAMESERVERS: "pending_nameservers",
  ISSUING_CERTIFICATE: "issuing_certificate",
  ACTIVE: "active",
  DISCONNECTED: "disconnected",
  FAILED: "failed",
};

/**
 * A domain is set up. Its panel stays open with the integration switched off —
 * only buying a new domain needs the fee, so a live one is never locked out.
 */
export const PRIVATE_DOMAIN_SET_UP_STATUSES = [
  PRIVATE_DOMAIN_STATUS.REGISTERING,
  PRIVATE_DOMAIN_STATUS.PENDING_NAMESERVERS,
  PRIVATE_DOMAIN_STATUS.ISSUING_CERTIFICATE,
  PRIVATE_DOMAIN_STATUS.ACTIVE,
];

/** How the domain came here: registered here, or owned elsewhere with its nameservers pointed here. */
export const PRIVATE_DOMAIN_METHOD = {
  REGISTER: "register",
  CONNECT: "connect",
};

/** The values `zone_status` and `ssl_status` take; both are null until the part exists. */
export const PRIVATE_DOMAIN_READINESS = {
  PENDING: "pending",
  ACTIVE: "active",
};

export const PRIVATE_DOMAIN_VIEW = {
  CONNECT: "connect",
  SETUP: "setup",
  MANAGE: "manage",
};

const PRIVATE_DOMAIN_VIEW_BY_STATUS = {
  [PRIVATE_DOMAIN_STATUS.NOT_CONNECTED]: PRIVATE_DOMAIN_VIEW.CONNECT,
  [PRIVATE_DOMAIN_STATUS.DISCONNECTED]: PRIVATE_DOMAIN_VIEW.CONNECT,
  [PRIVATE_DOMAIN_STATUS.FAILED]: PRIVATE_DOMAIN_VIEW.CONNECT,
  [PRIVATE_DOMAIN_STATUS.REGISTERING]: PRIVATE_DOMAIN_VIEW.SETUP,
  [PRIVATE_DOMAIN_STATUS.PENDING_NAMESERVERS]: PRIVATE_DOMAIN_VIEW.SETUP,
  [PRIVATE_DOMAIN_STATUS.ISSUING_CERTIFICATE]: PRIVATE_DOMAIN_VIEW.SETUP,
  [PRIVATE_DOMAIN_STATUS.ACTIVE]: PRIVATE_DOMAIN_VIEW.MANAGE,
};

/**
 * The screen a status opens on. A status this file does not know yet opens the
 * connect view: whatever it offers that does not apply, the API refuses (409).
 */
export const resolvePrivateDomainView = (status) =>
  PRIVATE_DOMAIN_VIEW_BY_STATUS[status] ?? PRIVATE_DOMAIN_VIEW.CONNECT;

/** The two ways to put the website on a domain, as the connect view's switch offers them. */
export const PRIVATE_DOMAIN_CONNECT_MODE = {
  REGISTER: "register",
  OWN: "own",
};

export const PRIVATE_DOMAIN_SETUP_STEP = {
  DOMAIN: "domain",
  DNS: "dns",
  CERTIFICATE: "certificate",
  LIVE: "live",
};

export const PRIVATE_DOMAIN_SETUP_STEP_ORDER = [
  PRIVATE_DOMAIN_SETUP_STEP.DOMAIN,
  PRIVATE_DOMAIN_SETUP_STEP.DNS,
  PRIVATE_DOMAIN_SETUP_STEP.CERTIFICATE,
  PRIVATE_DOMAIN_SETUP_STEP.LIVE,
];

/** The step a domain that is still being set up is on. */
export const PRIVATE_DOMAIN_CURRENT_SETUP_STEP = {
  [PRIVATE_DOMAIN_STATUS.REGISTERING]: PRIVATE_DOMAIN_SETUP_STEP.DOMAIN,
  [PRIVATE_DOMAIN_STATUS.PENDING_NAMESERVERS]: PRIVATE_DOMAIN_SETUP_STEP.DNS,
  [PRIVATE_DOMAIN_STATUS.ISSUING_CERTIFICATE]:
    PRIVATE_DOMAIN_SETUP_STEP.CERTIFICATE,
};

const PILL_CLASS = {
  SUCCESS: "bg-success-bg-soft text-success",
  INFO: "bg-info-bg-soft text-info",
  WARNING: "bg-warning-light text-warning-text",
  DANGER: "bg-danger-bg-soft text-danger",
  NEUTRAL: "bg-surface-muted text-text-muted",
};

// Waiting on the office reads as a warning; waiting on the registry does not.
const PRIVATE_DOMAIN_STATUS_PILL = {
  [PRIVATE_DOMAIN_STATUS.REGISTERING]: PILL_CLASS.INFO,
  [PRIVATE_DOMAIN_STATUS.PENDING_NAMESERVERS]: PILL_CLASS.WARNING,
  [PRIVATE_DOMAIN_STATUS.ISSUING_CERTIFICATE]: PILL_CLASS.INFO,
};

export const resolvePrivateDomainStatusPill = (status) =>
  PRIVATE_DOMAIN_STATUS_PILL[status] ?? PILL_CLASS.NEUTRAL;

/** A registrar transfer's `transfer.state`. */
export const PRIVATE_DOMAIN_TRANSFER_STATE = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  BLOCKED: "blocked",
  ACTION_REQUIRED: "action_required",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
};

/** The states the transfer card reports; a completed move is told by the history instead. */
export const PRIVATE_DOMAIN_SHOWN_TRANSFER_STATES = [
  PRIVATE_DOMAIN_TRANSFER_STATE.PENDING,
  PRIVATE_DOMAIN_TRANSFER_STATE.IN_PROGRESS,
  PRIVATE_DOMAIN_TRANSFER_STATE.BLOCKED,
  PRIVATE_DOMAIN_TRANSFER_STATE.ACTION_REQUIRED,
  PRIVATE_DOMAIN_TRANSFER_STATE.FAILED,
];

export const PRIVATE_DOMAIN_TRANSFER_STATE_META = {
  [PRIVATE_DOMAIN_TRANSFER_STATE.PENDING]: {
    labelKey: "privateDomain.transfer.state.pending",
    hintKey: "privateDomain.transfer.stateHint.pending",
    badgeClass: PILL_CLASS.INFO,
    icon: faClock,
  },
  [PRIVATE_DOMAIN_TRANSFER_STATE.IN_PROGRESS]: {
    labelKey: "privateDomain.transfer.state.in_progress",
    hintKey: "privateDomain.transfer.stateHint.in_progress",
    badgeClass: PILL_CLASS.INFO,
    icon: faRightLeft,
  },
  [PRIVATE_DOMAIN_TRANSFER_STATE.BLOCKED]: {
    labelKey: "privateDomain.transfer.state.blocked",
    hintKey: "privateDomain.transfer.stateHint.blocked",
    badgeClass: PILL_CLASS.WARNING,
    icon: faTriangleExclamation,
  },
  [PRIVATE_DOMAIN_TRANSFER_STATE.ACTION_REQUIRED]: {
    labelKey: "privateDomain.transfer.state.action_required",
    hintKey: "privateDomain.transfer.stateHint.action_required",
    badgeClass: PILL_CLASS.WARNING,
    icon: faTriangleExclamation,
  },
  [PRIVATE_DOMAIN_TRANSFER_STATE.FAILED]: {
    labelKey: "privateDomain.transfer.state.failed",
    hintKey: "privateDomain.transfer.stateHint.failed",
    badgeClass: PILL_CLASS.DANGER,
    icon: faCircleXmark,
  },
};

/** How often the overview is read again while something is still moving, in ms. */
export const PRIVATE_DOMAIN_POLL_MS = 30 * 1000;

const PRIVATE_DOMAIN_POLLED_STATUSES = [
  PRIVATE_DOMAIN_STATUS.REGISTERING,
  PRIVATE_DOMAIN_STATUS.PENDING_NAMESERVERS,
  PRIVATE_DOMAIN_STATUS.ISSUING_CERTIFICATE,
];

const PRIVATE_DOMAIN_POLLED_TRANSFER_STATES = [
  PRIVATE_DOMAIN_TRANSFER_STATE.PENDING,
  PRIVATE_DOMAIN_TRANSFER_STATE.IN_PROGRESS,
  PRIVATE_DOMAIN_TRANSFER_STATE.BLOCKED,
];

/** A setup or a registrar transfer that the backend is still carrying forward on its own. */
export const isPrivateDomainSettling = (overview) =>
  PRIVATE_DOMAIN_POLLED_STATUSES.includes(overview?.status) ||
  PRIVATE_DOMAIN_POLLED_TRANSFER_STATES.includes(overview?.transfer?.state);

/** `history[].event`, newest first in the payload. */
export const PRIVATE_DOMAIN_HISTORY_EVENT = {
  REGISTERED: "registered",
  ZONE_CREATED: "zone_created",
  NAMESERVERS_ACTIVE: "nameservers_active",
  CERTIFICATE_ISSUED: "certificate_issued",
  LIVE: "live",
  DISCONNECTED: "disconnected",
  TRANSFER_STARTED: "transfer_started",
  TRANSFER_COMPLETED: "transfer_completed",
  TRANSFER_FAILED: "transfer_failed",
  REGISTRATION_FAILED: "registration_failed",
  RENEWAL_PAID: "renewal_paid",
  RENEWAL_LAPSED: "renewal_lapsed",
  RENEWAL_REFUNDED: "renewal_refunded",
};

const PRIVATE_DOMAIN_HISTORY_DOT = {
  [PRIVATE_DOMAIN_HISTORY_EVENT.LIVE]: "bg-success",
  [PRIVATE_DOMAIN_HISTORY_EVENT.TRANSFER_COMPLETED]: "bg-success",
  [PRIVATE_DOMAIN_HISTORY_EVENT.DISCONNECTED]: "bg-text-subtle",
  [PRIVATE_DOMAIN_HISTORY_EVENT.TRANSFER_FAILED]: "bg-danger",
  [PRIVATE_DOMAIN_HISTORY_EVENT.REGISTRATION_FAILED]: "bg-danger",
  [PRIVATE_DOMAIN_HISTORY_EVENT.RENEWAL_PAID]: "bg-success",
  [PRIVATE_DOMAIN_HISTORY_EVENT.RENEWAL_LAPSED]: "bg-danger",
};

/** An event's dot colour; the steps along the way take the neutral info blue. */
export const resolvePrivateDomainHistoryDot = (event) =>
  PRIVATE_DOMAIN_HISTORY_DOT[event] ?? "bg-info";

/** The longest name the domain search accepts, after trimming. */
export const PRIVATE_DOMAIN_SEARCH_MAX = 63;

/** A domain example for placeholders — the same in both languages. */
export const PRIVATE_DOMAIN_EXAMPLE = "example.com.sa";

/** A name and an extension, no spaces; the API itself reduces a URL or a www. form to the bare host. */
export const PRIVATE_DOMAIN_INPUT_PATTERN = /^\S+\.\S+$/;

/**
 * The registrant's country when the prefill names none. The owner form has no
 * country picker — the app has no ISO alpha-2 list to offer — and every office
 * this serves is Saudi.
 */
export const PRIVATE_DOMAIN_DEFAULT_COUNTRY = "SA";

/** Registries take the owner in English letters; the API refuses anything else (same pattern there). */
export const PRIVATE_DOMAIN_LATIN_PATTERN = /^[\x20-\x7E]*$/;

/** `renewal.state`: where next year's wallet charge stands (`PrivateDomainSettings::RENEWAL_*`). */
export const PRIVATE_DOMAIN_RENEWAL_STATE = {
  NOT_DUE: "not_due",
  UNPAID: "unpaid",
  PAID: "paid",
  LAPSED: "lapsed",
  REFUNDED: "refunded",
};

const PRIVATE_DOMAIN_RENEWAL_META = {
  [PRIVATE_DOMAIN_RENEWAL_STATE.NOT_DUE]: {
    tone: NOTICE_TONE.INFO,
    messageKey: "privateDomain.renewal.state.not_due",
  },
  [PRIVATE_DOMAIN_RENEWAL_STATE.UNPAID]: {
    tone: NOTICE_TONE.WARNING,
    messageKey: "privateDomain.renewal.state.unpaid",
  },
  [PRIVATE_DOMAIN_RENEWAL_STATE.PAID]: {
    tone: NOTICE_TONE.SUCCESS,
    messageKey: "privateDomain.renewal.state.paid",
  },
  [PRIVATE_DOMAIN_RENEWAL_STATE.LAPSED]: {
    tone: NOTICE_TONE.DANGER,
    messageKey: "privateDomain.renewal.state.lapsed",
  },
  [PRIVATE_DOMAIN_RENEWAL_STATE.REFUNDED]: {
    tone: NOTICE_TONE.INFO,
    messageKey: "privateDomain.renewal.state.refunded",
  },
};

/** A state the API added before this file knew it reads as not due, never as a crash. */
export const resolvePrivateDomainRenewalMeta = (state) =>
  PRIVATE_DOMAIN_RENEWAL_META[state] ??
  PRIVATE_DOMAIN_RENEWAL_META[PRIVATE_DOMAIN_RENEWAL_STATE.NOT_DUE];

/** The renewal settings `PUT …/private-domain/settings` accepts, by field name. */
export const PRIVATE_DOMAIN_SETTING = {
  AUTO_RENEW: "auto_renew",
  LOCKED: "locked",
};

/** Why a searched domain cannot be selected (`domains[].reason`). */
export const DOMAIN_SEARCH_REASON = {
  DOMAIN_UNAVAILABLE: "domain_unavailable",
  DOMAIN_PREMIUM: "domain_premium",
  EXTENSION_NOT_SUPPORTED: "extension_not_supported",
  PRICE_ABOVE_INCLUDED: "price_above_included",
};

/** What a search result row says about its domain. */
export const DOMAIN_SEARCH_RESULT = {
  AVAILABLE: "available",
  TAKEN: "taken",
  PREMIUM: "premium",
  NOT_OFFERED: "not_offered",
  NOT_INCLUDED: "not_included",
  UNAVAILABLE: "unavailable",
};

const DOMAIN_SEARCH_RESULT_BY_REASON = {
  [DOMAIN_SEARCH_REASON.DOMAIN_UNAVAILABLE]: DOMAIN_SEARCH_RESULT.TAKEN,
  [DOMAIN_SEARCH_REASON.DOMAIN_PREMIUM]: DOMAIN_SEARCH_RESULT.PREMIUM,
  [DOMAIN_SEARCH_REASON.EXTENSION_NOT_SUPPORTED]:
    DOMAIN_SEARCH_RESULT.NOT_OFFERED,
  [DOMAIN_SEARCH_REASON.PRICE_ABOVE_INCLUDED]:
    DOMAIN_SEARCH_RESULT.NOT_INCLUDED,
};

/** Only an available domain the activation fee covers can be picked; a reason this file does not know reads as unavailable. */
export const resolveDomainSearchResult = (domain) => {
  if (domain?.available && domain?.included) {
    return DOMAIN_SEARCH_RESULT.AVAILABLE;
  }
  return (
    DOMAIN_SEARCH_RESULT_BY_REASON[domain?.reason] ??
    DOMAIN_SEARCH_RESULT.UNAVAILABLE
  );
};

export const DOMAIN_SEARCH_RESULT_META = {
  [DOMAIN_SEARCH_RESULT.AVAILABLE]: {
    labelKey: "privateDomain.search.result.available",
    badgeClass: PILL_CLASS.SUCCESS,
    icon: faCircleCheck,
  },
  [DOMAIN_SEARCH_RESULT.TAKEN]: {
    labelKey: "privateDomain.search.result.taken",
    badgeClass: PILL_CLASS.NEUTRAL,
    icon: faCircleXmark,
  },
  [DOMAIN_SEARCH_RESULT.PREMIUM]: {
    labelKey: "privateDomain.search.result.premium",
    badgeClass: PILL_CLASS.NEUTRAL,
    icon: faGem,
  },
  [DOMAIN_SEARCH_RESULT.NOT_OFFERED]: {
    labelKey: "privateDomain.search.result.notOffered",
    badgeClass: PILL_CLASS.WARNING,
    icon: faCircleInfo,
  },
  [DOMAIN_SEARCH_RESULT.NOT_INCLUDED]: {
    labelKey: "privateDomain.search.result.notIncluded",
    badgeClass: PILL_CLASS.WARNING,
    icon: faCircleInfo,
  },
  [DOMAIN_SEARCH_RESULT.UNAVAILABLE]: {
    labelKey: "privateDomain.search.result.unavailable",
    badgeClass: PILL_CLASS.NEUTRAL,
    icon: faCircleXmark,
  },
};

/** The record types the API accepts on create and update. */
export const DNS_RECORD_TYPE = {
  A: "A",
  AAAA: "AAAA",
  CNAME: "CNAME",
  MX: "MX",
  TXT: "TXT",
};

export const getDnsRecordTypeOptions = () =>
  Object.values(DNS_RECORD_TYPE).map((type) => ({ value: type, label: type }));

/** A record of another type (an SRV or CAA copied from the old DNS) can be deleted here, not edited. */
export const isEditableDnsRecordType = (type) =>
  Object.values(DNS_RECORD_TYPE).includes(type);

/** An example value per type, for the content placeholder — the same in both languages. */
export const DNS_RECORD_CONTENT_EXAMPLE = {
  [DNS_RECORD_TYPE.A]: "192.0.2.10",
  [DNS_RECORD_TYPE.AAAA]: "2001:db8::10",
  [DNS_RECORD_TYPE.CNAME]: "ghs.googlehosted.com",
  [DNS_RECORD_TYPE.MX]: "aspmx.l.google.com",
  [DNS_RECORD_TYPE.TXT]: "v=spf1 include:_spf.google.com ~all",
};

/** A record's `name` for the domain itself rather than a sub-name. */
export const DNS_ROOT_NAME = "@";

export const DNS_MX_PRIORITY_MAX = 65535;

/** `ttl` 1 means automatic; any other value is seconds, 60 to 86400. */
export const DNS_TTL_AUTO = 1;

const DNS_TTL_OPTIONS = [
  { value: DNS_TTL_AUTO, labelKey: "privateDomain.ttl.auto" },
  { value: 60, labelKey: "privateDomain.ttl.oneMinute" },
  { value: 300, labelKey: "privateDomain.ttl.fiveMinutes" },
  { value: 1800, labelKey: "privateDomain.ttl.thirtyMinutes" },
  { value: 3600, labelKey: "privateDomain.ttl.oneHour" },
  { value: 43200, labelKey: "privateDomain.ttl.twelveHours" },
  { value: 86400, labelKey: "privateDomain.ttl.oneDay" },
];

export const getDnsTtlOptions = (t) =>
  DNS_TTL_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }));

/** A TTL as words; a value outside the offered list (set elsewhere) prints as seconds. */
export const formatDnsTtl = (t, ttl) => {
  const option = DNS_TTL_OPTIONS.find(({ value }) => value === ttl);
  return option
    ? t(option.labelKey)
    : t("privateDomain.ttl.seconds", { value: ttl });
};

/** What happens to a record when the nameservers switch over. */
export const COPIED_RECORD_OUTCOME = {
  KEPT: "kept",
  WEBSITE: "website",
};

export const COPIED_RECORD_OUTCOME_META = {
  [COPIED_RECORD_OUTCOME.KEPT]: {
    labelKey: "privateDomain.copiedRecords.kept",
    badgeClass: PILL_CLASS.SUCCESS,
    icon: faCheck,
  },
  [COPIED_RECORD_OUTCOME.WEBSITE]: {
    labelKey: "privateDomain.copiedRecords.website",
    badgeClass: PILL_CLASS.INFO,
    icon: faGlobe,
  },
};

export const SADQ_INVITATION_LANGUAGE = {
  ENGLISH: 0,
  ARABIC: 1,
};

export const getSadqInvitationLanguageOptions = (t) => [
  {
    value: SADQ_INVITATION_LANGUAGE.ARABIC,
    label: t("sadqSettings.defaults.languageArabic"),
  },
  {
    value: SADQ_INVITATION_LANGUAGE.ENGLISH,
    label: t("sadqSettings.defaults.languageEnglish"),
  },
];

export const SADQ_AUTHENTICATION_TYPE = {
  DEFAULT: 0,
  NAFATH: 7,
};

export const getSadqAuthenticationTypeOptions = (t) => [
  {
    value: SADQ_AUTHENTICATION_TYPE.DEFAULT,
    label: t("sadqSettings.defaults.authDefault"),
  },
  {
    value: SADQ_AUTHENTICATION_TYPE.NAFATH,
    label: t("sadqSettings.defaults.authNafath"),
  },
];

export const SETTINGS_HUB_URL = `/${LANGUAGE}/settings`;

/**
 * Each tab's segment of its route, `settings/:tab`. A tab is a page of its own
 * rather than `?tab=` on the hub, so Back from it follows the breadcrumb trail
 * like any other page. These segments are part of the URL contract — renaming
 * one breaks bookmarked links.
 */
export const SETTINGS_TAB_PATHS = {
  [SETTINGS_TAB_NAMES.GENERAL_SETTINGS]: "general-settings",
  [SETTINGS_TAB_NAMES.COMMUNICATION_CHANNELS]: "communication-channels",
  [SETTINGS_TAB_NAMES.DOCUMENTS_PREFIXES]: "documents-prefixes",
  [SETTINGS_TAB_NAMES.BILL_INFO]: "bill-info",
  [SETTINGS_TAB_NAMES.CONTRACT_SERIAL_PREFIXES]: "contract-serial-prefixes",
  [SETTINGS_TAB_NAMES.HOME_SET]: "home-set",
  [SETTINGS_TAB_NAMES.LANDING_PAGE_CONTROLS]: "landing-page-controls",
  [SETTINGS_TAB_NAMES.PAYMENT_GATEWAY_SETUP]: "payment-gateway",
  [SETTINGS_TAB_NAMES.ZACTA]: "zatca",
  [SETTINGS_TAB_NAMES.EJAR_CREDENTIALS]: "ejar-credentials",
  [SETTINGS_TAB_NAMES.CRM_SETTINGS]: "crm",
  [SETTINGS_TAB_NAMES.SITE_VISIT_SETTINGS]: "site-visits",
  [SETTINGS_TAB_NAMES.NAFATH]: "nafath",
  [SETTINGS_TAB_NAMES.HR_SETTINGS]: "hr",
  [SETTINGS_TAB_NAMES.ADS_CROSS_LISTING]: "ads-cross-listing",
  [SETTINGS_TAB_NAMES.LETTER_SETTINGS]: "letters",
  [SETTINGS_TAB_NAMES.DATA_MIGRATION]: "data-migration",
  [SETTINGS_TAB_NAMES.SADQ]: "sadq",
  [SETTINGS_TAB_NAMES.FISCAL_YEAR]: "fiscal-year",
  [SETTINGS_TAB_NAMES.MAINTENANCE_SETTINGS]: "maintenance",
  [SETTINGS_TAB_NAMES.SMS_PROVIDER]: "sms-provider",
  [SETTINGS_TAB_NAMES.PRIVATE_DOMAIN]: "private-domain",
};

/**
 * The panels that read a section of the shared `dashboard/agent/settings`
 * payload. Every other panel loads its own data, so the payload is not fetched
 * for it — the module routes that reuse a panel (`site-visit-booking/settings`)
 * are gated on that module's permission, not on the one this endpoint needs.
 */
export const SHARED_PAYLOAD_TABS = Object.freeze([
  SETTINGS_TAB_NAMES.GENERAL_SETTINGS,
  SETTINGS_TAB_NAMES.DOCUMENTS_PREFIXES,
  SETTINGS_TAB_NAMES.CONTRACT_SERIAL_PREFIXES,
  SETTINGS_TAB_NAMES.COMMUNICATION_CHANNELS,
  SETTINGS_TAB_NAMES.BILL_INFO,
  SETTINGS_TAB_NAMES.HOME_SET,
  SETTINGS_TAB_NAMES.LANDING_PAGE_CONTROLS,
]);

export const settingsTabUrl = (tabName) =>
  `${SETTINGS_HUB_URL}/${SETTINGS_TAB_PATHS[tabName]}`;

/** The tab a `settings/:tab` segment names, or undefined for an unknown one. */
export const settingsTabNameFromPath = (path) =>
  Object.keys(SETTINGS_TAB_PATHS).find(
    (name) => SETTINGS_TAB_PATHS[name] === path?.toLowerCase()
  );

export const CROSS_LISTING_STATUS = {
  PENDING: "pending",
  PUBLISHED: "published",
  CLOSED: "closed",
  FAILED: "failed",
};

export const SETTINGS_CATEGORIES = {
  ORGANIZATION: "organization",
  MARKETING: "marketing",
  INTEGRATIONS: "integrations",
};

export const TAB_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  LOCKED: "locked",
};

export const DATA_MIGRATION_STATUS = {
  UPLOADED: "uploaded",
  VALIDATING: "validating",
  VALIDATED: "validated",
  REJECTED: "rejected",
  IMPORTING: "importing",
  COMPLETED: "completed",
  FAILED: "failed",
};

export const DATA_MIGRATION_IN_PROGRESS_STATUSES = Object.freeze([
  DATA_MIGRATION_STATUS.UPLOADED,
  DATA_MIGRATION_STATUS.VALIDATING,
  DATA_MIGRATION_STATUS.IMPORTING,
]);

export const DATA_MIGRATION_UNCOUNTED_STATUSES = Object.freeze([
  DATA_MIGRATION_STATUS.UPLOADED,
  DATA_MIGRATION_STATUS.VALIDATING,
]);

export const DATA_MIGRATION_FINDING_LEVEL = {
  ERROR: "error",
  WARNING: "warning",
};

export const DATA_MIGRATION_FINDING_FILTER_ALL = "all";

export const DATA_MIGRATION_FILE_EXTENSION = ".xlsx";

/** The data-migration tools, mirroring `App\Enums\DataMigration\DataMigrationTool`. */
export const DATA_MIGRATION_TOOL = {
  PORTFOLIO: "portfolio",
  CUSTOMERS: "customers",
  CRM_LEADS: "crm_leads",
  INVENTORY_PRODUCTS: "inventory_products",
  OPENING_BALANCES: "opening_balances",
  EMPLOYEES: "employees",
};

/**
 * Each tool's segment of its page, `settings/data-migration/:tool`. Part of the
 * URL contract — renaming one breaks bookmarked links.
 */
export const DATA_MIGRATION_TOOL_PATHS = {
  [DATA_MIGRATION_TOOL.PORTFOLIO]: "units-contracts-payments",
  [DATA_MIGRATION_TOOL.CUSTOMERS]: "customers",
  [DATA_MIGRATION_TOOL.CRM_LEADS]: "crm-leads",
  [DATA_MIGRATION_TOOL.INVENTORY_PRODUCTS]: "inventory-products",
  [DATA_MIGRATION_TOOL.OPENING_BALANCES]: "opening-balances",
  [DATA_MIGRATION_TOOL.EMPLOYEES]: "employees",
};

export const DATA_MIGRATION_HUB_URL = settingsTabUrl(
  SETTINGS_TAB_NAMES.DATA_MIGRATION
);

export const dataMigrationToolUrl = (tool) =>
  `${DATA_MIGRATION_HUB_URL}/${DATA_MIGRATION_TOOL_PATHS[tool]}`;

/**
 * How the CRM leads tool assigns a row naming no employee, mirroring
 * `LeadMigrationImporter::ASSIGNMENT_MODES`.
 */
export const DATA_MIGRATION_LEAD_ASSIGNMENT = {
  RULES: "rules",
  UNASSIGNED: "unassigned",
};

/** The tool a `settings/data-migration/:tool` segment names, or undefined. */
export const dataMigrationToolFromPath = (path) =>
  Object.keys(DATA_MIGRATION_TOOL_PATHS).find(
    (tool) => DATA_MIGRATION_TOOL_PATHS[tool] === path?.toLowerCase()
  );
